import { useState } from "react";
import type { Patient } from "../../../../types";

interface PatientNameCellProps {
  patient: Patient;
}

/**
 * Patient-name cell with a click-to-copy MRN.
 *
 * @param props - {@link PatientNameCellProps}
 * @returns The composed name/MRN/tag cell
 */
export function PatientNameCell({ patient }: PatientNameCellProps) {
  const [copied, setCopied] = useState(false);

  /**
   * Copy the patient MRN without triggering row navigation.
   *
   * @param event - The click event from the MRN control.
   * @returns Nothing.
   */
  const handleCopyMrn = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(patient.mrn).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex flex-col gap-0.5">
      <div className="patient-name-cell__name text-xs font-medium text-gray-900 truncate">
        {patient.name}
      </div>
      <button
        type="button"
        className="patient-name-cell__mrn text-xs text-gray-500 select-text group/mrn inline-flex items-center gap-1 w-fit cursor-pointer"
        onClick={handleCopyMrn}
        onMouseDown={(event) => event.stopPropagation()}
        title="Click to copy MRN"
        aria-label={`Copy MRN ${patient.mrn}`}
      >
        <span>{patient.mrn}</span>
        {copied ? (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#22C55E"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="opacity-0 group-hover/mrn:opacity-100 transition-opacity"
            aria-hidden="true"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        )}
      </button>
    </div>
  );
}

export default PatientNameCell;
